import { MouseEvent } from 'react';
import { Card, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import type { CSSObject } from 'tss-react';
import { Photo } from '@watchduty/camera';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { getDeviceInfo } from 'state/localStorageTyped';
import { useTakePhoto } from './useTakePhoto';

type FilePickerProps = {
  message: string;
  onFilesPicked: (files: File | File[] | Photo) => void;
  onFilesInvalid: (error: string) => void;
  error?: string;
  disabled: boolean;
};

type StyleProps = {
  hasError: boolean;
  disabled: boolean;
};

const MAX_FILE_UPLOAD_BYTES = 5000000; // 5 MB
const ALLOWED_MIMETYPES = 'image/*';

const useStyles = makeStyles<StyleProps>()((theme, { hasError, disabled }) => ({
  root: {
    alignItems: 'center',
    backgroundColor: theme.palette.grey[200],
    borderColor: hasError ? theme.palette.error.main : theme.palette.grey[500],
    color: theme.palette.grey[900],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
      color: theme.palette.grey[100],
      borderColor: hasError
        ? theme.palette.error.main
        : theme.palette.grey[600],
    }),
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    minHeight: 115,
    justifyContent: 'center',
    padding: theme.spacing(3),
    position: 'relative',
    flexDirection: 'column',
    gap: 32,
    borderRadius: Number(theme.shape.borderRadius) * 1.34,
  } as CSSObject,
  description: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

export const FilePicker = (props: FilePickerProps): JSX.Element => {
  const { message, onFilesPicked, onFilesInvalid, error, disabled } = props;
  const { classes } = useStyles({ hasError: Boolean(error), disabled });
  const deviceInfo = getDeviceInfo();
  const takePhoto = useTakePhoto();
  const { t } = useTranslation();

  const handleDropRejected = (dropErrors: FileRejection[]): void => {
    // convert the list of react-dropzone errors into a single human-readable error string
    const errorCodes = dropErrors
      .map((drop) => drop.errors.map((e) => e.code))
      .flat();

    const DROP_ERROR_CODE_TO_TEXT: Record<FileError['code'], string> = {
      'file-too-large': t('filePicker.errors.tooLarge'),
      'file-invalid-type': t('filePicker.errors.invalidType'),
      default: t('filePicker.errors.default'),
    };

    onFilesInvalid(DROP_ERROR_CODE_TO_TEXT[errorCodes[0]]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: ALLOWED_MIMETYPES,
    onDropAccepted: (files: File[]) => onFilesPicked(files),
    onDropRejected: handleDropRejected,
    maxSize: MAX_FILE_UPLOAD_BYTES,
  });

  const handleNativeClick = async (): Promise<void> => {
    const image = await takePhoto();
    if (!image) return;
    onFilesPicked(image);
  };

  const handleClick = async (event: MouseEvent<HTMLElement>): Promise<void> => {
    if (deviceInfo?.isWeb) {
      // defer to useDropzone()
      return;
    }

    event.stopPropagation();
    await handleNativeClick();
  };

  return (
    <>
      <Card
        variant="outlined"
        className={classes.root}
        {...getRootProps({
          onClick: handleClick,
        })}
      >
        <input {...getInputProps()} disabled={disabled} />

        <Typography
          variant="body1"
          align="center"
          className={classes.description}
        >
          {message}
        </Typography>
      </Card>
      {error && (
        <Typography
          variant="caption"
          component="p"
          color="error"
          sx={{ marginTop: 1 }}
        >
          {error}
        </Typography>
      )}
    </>
  );
};
